import type { FC } from 'react';
import { useLocation, useMatch } from 'react-router';
import type { LoadedAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { useNavigationItems } from 'src/components/layout/app-shell/use-navigation-items';
import type { ActiveSection } from 'src/components/layout/header/types';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { useDirectFcfsFlags } from 'src/hooks/use-direct-fcfs-flags';
import { useOptionalAuthenticatedMonolithUser } from 'src/hooks/use-monolith-user';

const angularRoutes = [
  '#/start',
  '#/notifications',
  '#/uploads',
  '#/settings',
  '#/activity',
  '#/platform-settings',
];

export const isDashboardActive = (pathname: string, hash: string) =>
  pathname.startsWith('/start') ||
  pathname.startsWith('/activity') ||
  pathname.startsWith('/uploads') ||
  pathname.startsWith('/shippers') ||
  pathname.startsWith('/platform-settings') ||
  pathname.startsWith('/operators') ||
  pathname.startsWith('/shipper') ||
  pathname.startsWith('/dashboard') ||
  pathname.startsWith('/notifications') ||
  pathname.startsWith('/snam-bid-collector') ||
  pathname.startsWith('/direct-fcfs') ||
  (pathname.startsWith('/platform') &&
    angularRoutes.some((angularRoute) => hash.startsWith(angularRoute)));

type SecondaryNavDashboardProps = {
  activeSection: ActiveSection;
  appShellData: LoadedAppShellData;
};

export const SecondaryNavDashboard: FC<SecondaryNavDashboardProps> = ({
  activeSection,
  appShellData,
}) => {
  const monolithUser = useOptionalAuthenticatedMonolithUser();
  const { pathname, hash } = useLocation();
  const navigationItems = useNavigationItems(appShellData);

  const directFcfsFlags = useDirectFcfsFlags();
  const isDirectFcfsSection = useMatch('/direct-fcfs/*');
  const renderDirectFcfsLink =
    directFcfsFlags.data?._links?.['view-requests'] ||
    directFcfsFlags.data?._links?.['read-product'] ||
    directFcfsFlags.data?._links?.['read-capacity'] ||
    isDirectFcfsSection;

  if (!activeSection.dashboard) return null;

  return (
    <div
      className={`
        flex flex-col flex-wrap items-stretch gap-x-xl gap-y-md
        md:flex-row md:items-center
      `}
      data-testid="secondary-nav"
    >
      {monolithUser?.isShipper || monolithUser?.isTso ? (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.angularUrl}/#/start`}
        >
          Dashboard
        </ExternalLink>
      ) : (
        <Link
          mode="secondary"
          to="/dashboard"
          isActive={pathname === '/dashboard'}
        >
          Dashboard
        </Link>
      )}

      {navigationItems.topNav.timeline.notificationsNew ? (
        <Link
          mode="secondary"
          to="/notifications"
          isActive={
            (pathname.startsWith(`/platform`) && hash === '#/activity') ||
            pathname.startsWith(`/notifications`) ||
            (pathname.startsWith(`/platform`) && hash === '#/notifications')
          }
        >
          Your Timeline
        </Link>
      ) : (
        <>
          {navigationItems.topNav.timeline.shipperActivities && (
            <ExternalLink
              mode="secondary"
              href={`${PRISMA_CONFIG.angularUrl}/#/activity`}
            >
              Activity
            </ExternalLink>
          )}

          {navigationItems.topNav.timeline.notificationsOld && (
            <ExternalLink
              mode="secondary"
              href={`${PRISMA_CONFIG.angularUrl}/#/notifications`}
            >
              Notifications
            </ExternalLink>
          )}
        </>
      )}

      {monolithUser?.currentlyActingWithOrganisationRole === 'SHIPPER' && (
        <Link
          mode="secondary"
          to="/operators"
          isActive={
            pathname.startsWith('/operators') ||
            pathname.startsWith('/dashboard/operators')
          }
          data-help-annotation="assignments:2"
        >
          Operators
        </Link>
      )}

      {monolithUser?.isTso && (
        <ExternalLink
          mode="secondary"
          href={`${PRISMA_CONFIG.angularUrl}/#/uploads`}
        >
          Uploads
        </ExternalLink>
      )}

      {renderDirectFcfsLink && (
        <Link mode="secondary" to="/direct-fcfs">
          Manage Direct FCFS
        </Link>
      )}

      {/*
        Whoever has the "assignedUsers" link is allowed to see "Shippers",
        BUT if the user is known the the monolith and _can_ switch to the SSO or TSO role,
        they should switch to their operator role to see it.
      */}
      {appShellData.assignmentService?.response.data._links.assignedUsers &&
        monolithUser?.canSwitchToOrganisationRole !== 'SSO' &&
        monolithUser?.canSwitchToOrganisationRole !== 'TSO' && (
          <Link
            mode="secondary"
            to="/shippers"
            isActive={
              pathname.startsWith('/shippers') ||
              pathname.startsWith('/dashboard/shippers')
            }
          >
            Shippers
          </Link>
        )}

      {monolithUser?.isTso && (
        <Link mode="secondary" to="/market-messages/maintenance-messages">
          Maintenance Messages
        </Link>
      )}

      {appShellData.isSnamAdmin && (
        <Link mode="secondary" to="/snam-bid-collector">
          Bid Collector
        </Link>
      )}
    </div>
  );
};
